/* eslint-disable max-len */
/* eslint-disable prettier/prettier */
import { pageSize, SUPPORT_LANGUAGE } from '@/config/platform'
import { queryClassify, queryPlatformList } from '@/src/api/platform'
import Faq from '@/src/components/platform/Faq'
import Head from 'next/head'
import Layout from 'src/components/layout'
import LinkSeo from 'src/components/layout/linkSeo'
import MetaSeo from 'src/components/layout/metaSeo'
import Footer from 'src/components/platform/Footer'
import PlatFormList from 'src/components/platform/PlatFormList'
import useGA from 'src/hooks/use_ga'
import useI18n from 'src/hooks/use_i18n'
import { CarrierInfoMarkDTO, GlobalGA4TagType, TabItem } from 'src/types'
import { getV5Front } from 'src/utils/server_global'
import { DEFAULT_CARRIER_INFO_MARK } from '../../constants/carrier_info_mark'
import styles from './index.module.scss'

const logger = require('@/config/logger')

interface PageProps {
  carrierInfoMark: CarrierInfoMarkDTO
  country: string | any
  totalCarriersStr: string
  page: 'index' | number // page === index 默认首页，/brands/page-(number)为指定页
  classify: TabItem[]
  query: any
  platformData: {
    title: string
    keywords: string
    description: string
  }
}

export default function PlatformList({ carrierInfoMark, classify, platformData, query, country, page }: PageProps) {
  const pageType: GlobalGA4TagType = `brands_${page === 'index' ? 'index' : 'page'}`
  useGA(pageType)
  const { activeLocale: locale, $t } = useI18n()

  return (
    <>
      <MetaSeo
        keywords={$t('ResWwwPlatform.global.__PlatformKeywords')}
        title={$t('ResWwwPlatform.global.__PlatformTitle')}
        description={$t('ResWwwPlatform.global.__PlatformDescription')}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
            {
              "@context": "https://schema.org/",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Homepage",
                "item": "https://www.17track.net/${locale}"
              },{
                "@type": "ListItem",
                "position": 2,
                "name": "brands",
                "item": "https://www.17track.net/${locale}/brands${page === 'index' ? '' : `/page-${page}`}"
              }]
            }
          `
          }}
        />
      </Head>
      <LinkSeo TagType={pageType} routeLang={locale} TagTypeValue={page} />
      <Layout showAllPlatform carrierInfoMark={carrierInfoMark} country={country}>
        <div className={styles.platform}>
          <div className={styles.platformTitle}>
            <h1 id="yq_platform_tile" title={$t('ResWwwPlatform.global.__PlatformH1')}>
              {$t('ResWwwPlatform.global.__PlatformH1')}
            </h1>
            <p>{$t('ResWwwPlatform.global.__PlatformSubTitle')}</p>
          </div>
          <PlatFormList classify={classify} platformData={platformData} query={query} />
          <Faq faqList={$t('ResWwwPlatform.items')} />
          <Footer />
        </div>
      </Layout>
    </>
  )
}

interface Props {
  locale: string
  query: { t?: string; c?: string }
  req: any
}

export async function getServerSideProps({ locale, query = {}, req }: Props) {
  try {
    // 目前只处理一级路由，二级,你所看到的nodePage和nodeLang是指由express直接转发渲染过来传递的数据
    const page = req.queryParams?.nodePage || 'index'
    const lang = locale || req.queryParams?.nodeLang || 'en'
    // 绕过是走页数url即page, 运输商等二级文件路由走name, 首页index
    const { 'cf-ipcountry': country } = req.headers
    logger.info(`platform-${page}请求header：`, req.headers)
    const { t = '', c = '' } = query
    const params = {
      title: t,
      classify_id: c,
      page_num: page === 'index' ? 1 : page,
      page_size: pageSize,
      language: locale
    }

    // 语言不在支持列表中，重定向到英文
    if (!SUPPORT_LANGUAGE.includes(lang)) {
      return {
        redirect: {
          destination: req.originalUrl?.replace(lang, 'en'),
          permanent: false // or true, based on your requirement
        }
      }
    }

    const [V5Front, carrierInfoMark, classify, platformRes] = await Promise.all([
      getV5Front('www', lang),
      fetch(`https://res.17track.net/asset/carrier/info/mark.${lang}.json`)
        .then((res) => res.json())
        .catch((error) => {
          logger.error('platform页面获取carrierInfoMark信息异常', { message: error.message })
          return DEFAULT_CARRIER_INFO_MARK // 兜底数据
        }),
      queryClassify({ language: lang })
        .then((res) =>
          (res?.data || []).map(({ classify_id, ...others }) => ({
            key: classify_id,
            ...others
          }))
        )
        .catch((error) => {
          logger.error('platform页面获取分类信息异常', { message: error.message })
          return []
        }),
      queryPlatformList(params).catch((error) => {
        logger.error('platform页面获取platform列表信息异常', { message: error.message, params })
        return { list: [] }
      })
    ])
    classify.unshift({ name: 'All', key: '' })
    logger.info(`platform-${page}获取carrierInfoMark信息`, { carrierInfoMark })
    logger.info(`platform-${page}请求platformData：`, { data: platformRes, params })
    // 服务端只取一页运输商数据
    return {
      props: {
        locale: lang,
        V5Front,
        carrierInfoMark,
        page,
        classify,
        query,
        platformData: platformRes?.data,
        country: country ?? 'CN'
      }
    }
  } catch (error: any) {
    logger.error('platform页面请求异常', { message: error.message })
  }
}
